import { ITEM_TYPE_MODEL, resolveItemTypeKey } from '@constants/itemTypes';

/**
 * The only module in ui/src that may contain a bricklink.com literal.
 * The image-funnel ESLint rule enforces that.
 *
 * Verified against img.bricklink.com with live requests:
 *   ItemImage/{code}/0/{n}.png   200 for SN MN IN ON BN CN GN, and PN for sticker sheets
 *   ItemImage/PN/{colorId}/{n}.png  200 for a part in a colour it exists in
 *   ItemImage/PN/0/3001.png      404 — a real part has no colour 0
 *   ItemImage/{P|G}L/{n}.png     200, colour-agnostic, for a part or gear with no known colour
 *   ItemImage/PL/8643stk01.png   404 — sticker sheets are not in the part library
 *
 * The colour-agnostic path is ItemImage/{P|G}L/{n}.png and not the older
 * {P|G}L/{n}.jpg, because that older path hardcodes an extension BrickLink
 * does not use for every item. Re-verified 2026-09-06: PL/47506.jpg is a 404
 * while PL/47506.gif is a 200, and BrickLink's own API advertises the stale
 * PL/30396.jpg, also a 404. ItemImage/{P|G}L/{n}.png is extension-agnostic and
 * served 200 for every item the .jpg path served plus the ones it missed.
 */
const IMAGE_HOST = 'https://img.bricklink.com';
const CATALOG_HOST = 'https://www.bricklink.com';

/**
 * Strips the local uniqueness suffix BrickBuddy stores on INSTRUCTION and
 * ORIGINAL_BOX numbers. Idempotent and case-insensitive: 371 of 406 prod
 * INSTRUCTION rows carry no suffix, all 38 ORIGINAL_BOX rows do, and item
 * numbers are citext so stored casing varies.
 *
 * Mirrors toBricklinkLookupItemNumber in
 * api/services/BrickBuddy/CatalogLookupService.js.
 *
 * @param {string} itemType - A BrickBuddy type name or BrickLink letter code
 * @param {string} itemNumber - The stored item number
 * @returns {string|null} The number BrickLink knows the item by
 */
export const toBricklinkLookupNumber = (itemType, itemNumber) => {
    if (!itemNumber) return null;

    const typeKey = resolveItemTypeKey(itemType);
    if (!typeKey) return String(itemNumber);

    const suffix = ITEM_TYPE_MODEL[typeKey].storedSuffix;
    if (!suffix) return String(itemNumber);

    const number = String(itemNumber);
    return number.toLowerCase().endsWith(suffix)
        ? number.slice(0, -suffix.length)
        : number;
};

const getStoredMinifigureNumber = (item) => {
    const storedUrls = [item.thumbnail_url, item.image_url];

    for (const storedUrl of storedUrls) {
        if (typeof storedUrl !== 'string') continue;
        const match = storedUrl.match(/(?:\/M(?:L)?\/|\/ItemImage\/MN\/0\/)([^/?#]+?)(?:\.(?:jpe?g|png|webp))?(?:[?#]|$)/i);
        if (match) return match[1];
    }

    return null;
};

/**
 * Resolves the BrickLink lookup number from an item object.
 *
 * MINIFIG uses the number encoded in its stored BrickLink image path when
 * available. That path identifies the hosted image even when alternate_number
 * points at a catalog alias without an image.
 *
 * @param {Object} item - An item-shaped object
 * @returns {string|null} The number BrickLink knows the item by
 */
export const getBricklinkLookupNumber = (item) => {
    if (!item || typeof item !== 'object') return null;

    const itemType = item.item_type || item.type;
    const typeKey = resolveItemTypeKey(itemType);

    const storedNumber = typeKey === 'MINIFIG'
        ? (getStoredMinifigureNumber(item) || item.alternate_number || item.item_number || item.number)
        : (item.item_number || item.number);

    return toBricklinkLookupNumber(itemType, storedNumber);
};

/**
 * Recovers a part's representative colour from a stored BrickLink thumbnail.
 *
 * Catalog search shows a part with no lot context, so it has no colour of its
 * own. BrickLink's stored thumbnail path carries one.
 *
 * @param {Object} item - An item-shaped object carrying thumbnail_url or image_url
 * @returns {number|null} The colour id, or null when no stored url carries one
 */
export const getRepresentativeColorId = (item) => {
    if (!item || typeof item !== 'object') return null;

    const storedUrl = item.thumbnail_url || item.image_url;
    if (!storedUrl || typeof storedUrl !== 'string') return null;

    const match = storedUrl.match(/(?:\/P\/|\/ItemImage\/PN\/)(\d+)\//);
    if (!match) return null;

    return Number(match[1]);
};

const getImageColorId = (item) => item.color_id ?? getRepresentativeColorId(item);

/**
 * Rejects a colour id that would silently produce a 404 URL.
 *
 * The string '0' and the number 0 are not interchangeable here: 0 selects the
 * colour-agnostic library path, '0' would interpolate into
 * ItemImage/PN/0/{n}.png, which BrickLink does not serve for a real part.
 * A bad colour id is a caller error, not row data, so it throws rather than
 * degrading to a broken image.
 */
const assertValidColorId = (colorId) => {
    if (colorId === null || colorId === undefined) return;
    if (Number.isInteger(colorId) && colorId >= 0) return;
    throw new Error(`colorId must be a non-negative integer, received: ${String(colorId)}`);
};

/**
 * Builds the BrickLink image URL from resolved primitives.
 *
 * Returns null rather than throwing for an unknown or unmapped type. The
 * callers run inside table cell renderers, and the only React error boundary
 * is app-wide, so a throw there unmounts the entire page instead of degrading
 * one cell to a placeholder.
 *
 * Not exported. getItemImageUrl(item) is the only public entry point.
 *
 * @param {Object} params
 * @param {string} params.itemType - A BrickBuddy type name or BrickLink letter code
 * @param {string} params.itemNumber - The stored item number
 * @param {number} [params.colorId] - The colour id, when the screen knows one
 * @returns {string|null} The image URL, or null when none can be built
 */
const buildItemImageUrl = ({ itemType, itemNumber, colorId = null }) => {
    assertValidColorId(colorId);

    if (!itemNumber) return null;

    const typeKey = resolveItemTypeKey(itemType);
    if (!typeKey) return null;

    const { imageCode, colorless } = ITEM_TYPE_MODEL[typeKey];
    if (!imageCode) return null;

    const number = toBricklinkLookupNumber(typeKey, itemNumber);

    if (colorless) {
        return `${IMAGE_HOST}/ItemImage/${imageCode}/0/${number}.png`;
    }

    if (colorId) {
        return `${IMAGE_HOST}/ItemImage/${imageCode}/${colorId}/${number}.png`;
    }

    // Colour-agnostic library image, for a coloured type whose colour is unknown.
    return `${IMAGE_HOST}/ItemImage/${imageCode[0]}L/${number}.png`;
};

/**
 * Builds the BrickLink image URL for an item-shaped object.
 *
 * This is the single function every BrickLink image display in the app calls
 * — parts, minifigures, sets, gear, instructions, sticker sheets, everything.
 *
 * @param {Object} item - An item-shaped object. Reads item_type/type,
 *   item_number/number, alternate_number, color_id, thumbnail_url, image_url.
 * @returns {string|null} The image URL, or null when none can be built
 */
export const getItemImageUrl = (item) => {
    if (!item || typeof item !== 'object') return null;

    return buildItemImageUrl({
        itemType: item.item_type || item.type,
        itemNumber: getBricklinkLookupNumber(item),
        colorId: getImageColorId(item)
    });
};

/**
 * Builds the BrickLink catalog page URL for an item.
 *
 * Carries the same stored-suffix strip as the image URL: without it, an
 * instruction row links to catalogitem.page?I=6387-1-instruction, which is a
 * dead page.
 *
 * @param {Object} params
 * @param {string} params.itemType - A BrickBuddy type name or BrickLink letter code
 * @param {string} params.itemNumber - The stored item number
 * @param {number} [params.colorId] - The colour id, for a colour-specific page
 * @returns {string|null} The catalog URL, or null when the type has no catalog code
 */
export const getItemBricklinkUrl = ({ itemType, itemNumber, colorId = null }) => {
    if (!itemNumber) return null;

    const typeKey = resolveItemTypeKey(itemType);
    if (!typeKey) return null;

    const { letter } = ITEM_TYPE_MODEL[typeKey];
    if (!letter) return null;

    const number = toBricklinkLookupNumber(typeKey, itemNumber);
    const page = `${CATALOG_HOST}/v2/catalog/catalogitem.page?${letter}=${number}`;

    return colorId ? `${page}#T=P&C=${colorId}` : page;
};

/**
 * Builds every BrickLink image URL an item could resolve to, primary number
 * first.
 *
 * BrickLink can fold a retired catalog number into a current item's
 * alternate_number while still hosting the item's only real photo under that
 * retired number rather than the current one — a merged shadow/canonical
 * catalog pair is one way this happens. A caller with no way to know in
 * advance which number has the photo can render the primary URL and retry the
 * rest in order when it 404s.
 *
 * @param {Object} params
 * @param {string} params.itemType - A BrickBuddy type name or BrickLink letter code
 * @param {string} params.itemNumber - The stored item number
 * @param {string} [params.alternateNumber] - Comma-separated alternate numbers, as stored on items.alternate_number
 * @param {number} [params.colorId] - The colour id, when the screen knows one
 * @returns {string[]} Candidate URLs in priority order, primary first; empty when no URL can be built
 */
export const getItemImageUrlCandidates = ({ itemType, itemNumber, alternateNumber = null, colorId = null }) => {
    const primary = buildItemImageUrl({ itemType, itemNumber, colorId });
    if (primary === null) return [];

    const alternateTokens = typeof alternateNumber === 'string'
        ? alternateNumber.split(',').map(token => token.trim()).filter(token => token.length > 0)
        : [];

    const seen = new Set([primary]);
    const candidates = [primary];

    alternateTokens.forEach(token => {
        const url = buildItemImageUrl({ itemType, itemNumber: token, colorId });
        if (url !== null && !seen.has(url)) {
            seen.add(url);
            candidates.push(url);
        }
    });

    return candidates;
};

/**
 * Resolves every BrickLink image URL candidate for an item-shaped object.
 *
 * @param {Object} item - An item-shaped object carrying item_number/number,
 *   item_type/type, and optionally alternate_number
 * @returns {string[]} Candidate URLs in priority order; empty when none can be built
 */
export const getDisplayImageUrlCandidates = (item) => {
    if (!item || typeof item !== 'object') return [];

    return getItemImageUrlCandidates({
        itemType: item.item_type || item.type,
        itemNumber: getBricklinkLookupNumber(item),
        alternateNumber: item.alternate_number,
        colorId: getImageColorId(item)
    });
};
